import { Button, Flex, Input, Text, Textarea } from "@chakra-ui/react";
import { Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { Alert } from "../../components/Alert";
import { usePaymentApproval, PaymentApproval } from "../../hooks/usePaymentApproval";
import { formatCurrency, formatDate, showToast } from "../../utils";
import { Section } from "./_Section";

export function PaymentApprovalSection() {
  const { id } = useParams();
  const { getPayments, approvePayment } = usePaymentApproval();
  const [payments, setPayments] = useState<PaymentApproval[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [approvalModalState, setApprovalModalState] = useState<boolean>(false);
  const [selectedPayment, setSelectedPayment] = useState<PaymentApproval | null>(null);
  const [password, setPassword] = useState<string>("");
  const [reproveReason, setReproveReason] = useState<string>("");
  const [isApproving, setIsApproving] = useState<boolean>(false);
  const ref = useRef();

  useEffect(() => {
    loadPayments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function loadPayments() {
    if (!id) return;
    try {
      setLoading(true);
      const response = await getPayments(id);
      setPayments(response?.data?.content || []);
    } catch (error: any) {
      error?.response?.data?.errors?.map((error: any) =>
        showToast({
          type: "error",
          message: `${error?.code} - ${error?.description}`,
        })
      );
    } finally {
      setLoading(false);
    }
  }

  function handleOpenApprovalModal(payment: PaymentApproval, approved: boolean) {
    setSelectedPayment({ ...payment, isApproved: approved } as any);
    setApprovalModalState(true);
    setPassword("");
    setReproveReason("");
  }

  async function handleApprove() {
    if (!selectedPayment || !password) {
      showToast({
        type: "error",
        message: "Por favor, informe a senha",
      });
      return;
    }

    try {
      setIsApproving(true);
      const approvalData: any = {
        uuid: selectedPayment.uuid,
        isApproved: (selectedPayment as any).isApproved,
        password: password,
      };
      
      if (!(selectedPayment as any).isApproved && reproveReason) {
        approvalData.reproveReason = reproveReason;
      }
      
      await approvePayment(id, approvalData);
      showToast({
        type: "success",
        message: `Pagamento ${(selectedPayment as any).isApproved ? "aprovado" : "rejeitado"} com sucesso!`,
      });
      setApprovalModalState(false);
      setPassword("");
      setReproveReason("");
      setSelectedPayment(null);
      await loadPayments();
    } catch (error: any) {
      error?.response?.data?.errors?.map((error: any) =>
        showToast({
          type: "error",
          message: `${error?.code} - ${error?.description}`,
        })
      );
    } finally {
      setIsApproving(false);
    }
  }

  const columns: ColumnsType<PaymentApproval> = [
    {
      key: "payerName",
      title: "Pagador",
      dataIndex: "payerName",
      render: (payerName: string) => <Text>{payerName}</Text>,
    },
    {
      key: "payerDocument",
      title: "Documento",
      dataIndex: "payerDocument",
      render: (payerDocument: string) => <Text>{payerDocument}</Text>,
    },
    {
      key: "amount",
      title: "Valor",
      dataIndex: "amount",
      render: (amount: number) => (
        <Text fontWeight="600">{formatCurrency({ value: amount })}</Text>
      ),
    },
    {
      key: "type",
      title: "Tipo",
      dataIndex: "type",
      render: (type: string) => <Text>{type}</Text>,
    },
    {
      key: "paymentDate",
      title: "Data do Pagamento",
      dataIndex: "paymentDate",
      render: (paymentDate: string) => (
        <Text>{formatDate(paymentDate)}</Text>
      ),
    },
    {
      key: "status",
      title: "Status",
      dataIndex: "status",
      render: (status: string) => (
        <Text
          color={
            status === "AWAITING_ANTIFRAUD_ANALYSIS"
              ? "#FAAD14"
              : status === "APPROVED"
              ? "#52C41A"
              : "#FF4D4F"
          }
          fontWeight="500"
        >
          {status === "AWAITING_ANTIFRAUD_ANALYSIS"
            ? "Aguardando Análise"
            : status === "APPROVED"
            ? "Aprovado"
            : "Rejeitado"}
        </Text>
      ),
    },
    {
      key: "actions",
      title: "Ações",
      render: (_, record: PaymentApproval) => (
        <Flex gap="8px">
          {record.status === "AWAITING_ANTIFRAUD_ANALYSIS" && (
            <>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => handleOpenApprovalModal(record, true)}
              >
                Aprovar
              </Button>
              <Button
                size="sm"
                variant="cancel"
                onClick={() => handleOpenApprovalModal(record, false)}
              >
                Rejeitar
              </Button>
            </>
          )}
        </Flex>
      ),
    },
  ];

  return (
    <>
      <Alert
        isOpen={approvalModalState}
        onClose={() => {
          setApprovalModalState(false);
          setPassword("");
          setReproveReason("");
          setSelectedPayment(null);
        }}
        title={
          (selectedPayment as any)?.isApproved
            ? "Aprovar pagamento?"
            : "Rejeitar pagamento?"
        }
        messageBody={
          <Flex flexDirection="column" gap="16px" mt="16px">
            <Text>
              {(selectedPayment as any)?.isApproved
                ? "Deseja aprovar este pagamento?"
                : "Deseja rejeitar este pagamento?"}
            </Text>
            {selectedPayment && (
              <Flex flexDirection="column" gap="8px">
                <Text fontWeight="600">Pagador: {selectedPayment.payerName}</Text>
                <Text fontWeight="600">
                  Valor: {formatCurrency({ value: selectedPayment.amount })}
                </Text>
              </Flex>
            )}
            <Input
              type="password"
              placeholder="Digite sua senha"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              mt="8px"
            />
            {!(selectedPayment as any)?.isApproved && (
              <Textarea
                placeholder="Motivo da rejeição"
                value={reproveReason}
                onChange={(e) => setReproveReason(e.target.value)}
                mt="8px"
                minH="80px"
              />
            )}
          </Flex>
        }
        confirmButtonText={
          (selectedPayment as any)?.isApproved ? "Aprovar" : "Rejeitar"
        }
        closeButtonText="Cancelar"
        onConfirm={handleApprove}
        leastDestructiveRef={ref}
        isLoading={isApproving}
      />
      <Section
        sectionTitle="Aprovação de pagamentos"
        onClickEditButton={() => {}}
        onClickConfirmButton={() => {}}
        buttons={[]}
        isEditButtonHidden
      >
        <Table<PaymentApproval>
          columns={columns}
          dataSource={payments}
          rowKey={(record) => record.uuid}
          loading={loading}
          locale={{ emptyText: "Sem pagamentos aguardando aprovação" }}
        />
      </Section>
    </>
  );
}
